<template>
  <div class="dm-entry">
    <div class="dm-entry__toolbar">
      <button type="button" class="small toolbar-btn" title="Bold" @click="insertMarkdown('**', '**', 'bold text')">
        <strong>B</strong>
      </button>
      <button type="button" class="small toolbar-btn" title="Italic" @click="insertMarkdown('*', '*', 'italic text')">
        <em>I</em>
      </button>
      <button type="button" class="small toolbar-btn" title="Inline code" @click="insertMarkdown('`', '`', 'code')">
        <code>{ }</code>
      </button>
      <button type="button" class="small toolbar-btn" title="Unordered list" @click="insertListItem">
        &bull; List
      </button>
      <span class="toolbar-sep"></span>
      <button
        type="button"
        class="small toolbar-btn"
        :class="{ active: showPreview }"
        @click="showPreview = !showPreview"
      >
        {{ showPreview ? 'Edit' : 'Preview' }}
      </button>
    </div>

    <div v-if="showPreview" class="dm-entry__preview">
      <MarkdownRenderer :content="body" />
      <p v-if="!body" class="preview-placeholder">Nothing to preview yet.</p>
    </div>

    <textarea
      v-else
      ref="textareaRef"
      v-model="body"
      class="dm-entry__textarea"
      :placeholder="$t('default.discussions.composer_placeholder')"
      :maxlength="MAX_LENGTH"
      rows="3"
      @keydown.ctrl.enter="handleSubmit"
      @keydown.meta.enter="handleSubmit"
    ></textarea>

    <div class="dm-entry__footer">
      <span class="char-count" :class="{ 'char-count--warn': charCountWarn }">
        {{ body.length }} / {{ MAX_LENGTH }}
      </span>
      <button
        type="button"
        class="green"
        :disabled="sending || !canSubmit"
        @click="handleSubmit"
      >
        {{ sending ? $t('default.discussions.submitting') : $t('default.discussions.send', 'Send') }}
      </button>
    </div>

    <div v-if="error" class="dm-entry__error">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import MarkdownRenderer from '@/components/shared/MarkdownRenderer'

const MAX_LENGTH = 10000

const props = defineProps({
  recipientId: {
    type: Number,
    required: true,
  },
  pageId: {
    type: Number,
    required: false,
    default: null,
  },
})

const emit = defineEmits(['sent'])

const store = useStore()

const body = ref('')
const showPreview = ref(false)
const textareaRef = ref(null)

const sending = computed(() => store.state.directMessages.sending)
const error = computed(() => store.state.directMessages.sendError)

const charCountWarn = computed(() => body.value.length > MAX_LENGTH * 0.9)
const canSubmit = computed(() => body.value.trim().length > 0)

async function handleSubmit() {
  if (!canSubmit.value || sending.value) return
  const sentMessage = await store.dispatch('directMessages/sendMessage', {
    recipientId: props.recipientId,
    content: body.value.trim(),
    pageId: props.pageId,
  })
  if (!error.value) {
    body.value = ''
    showPreview.value = false
    emit('sent', sentMessage)
  }
}

function insertMarkdown(prefix, suffix, placeholder) {
  const textarea = textareaRef.value
  if (!textarea) {
    body.value += `${prefix}${placeholder}${suffix}`
    return
  }
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const selected = body.value.substring(start, end) || placeholder
  const before = body.value.substring(0, start)
  const after = body.value.substring(end)
  body.value = `${before}${prefix}${selected}${suffix}${after}`
  const newPos = start + prefix.length + selected.length + suffix.length
  setTimeout(() => {
    textarea.focus()
    textarea.setSelectionRange(newPos, newPos)
  }, 0)
}

function insertListItem() {
  const textarea = textareaRef.value
  if (!textarea) {
    body.value += '\n- '
    return
  }
  const start = textarea.selectionStart
  const before = body.value.substring(0, start)
  const after = body.value.substring(start)
  const needsNewline = before.length > 0 && !before.endsWith('\n')
  const insertion = `${needsNewline ? '\n' : ''}- `
  body.value = `${before}${insertion}${after}`
  const newPos = start + insertion.length
  setTimeout(() => {
    textarea.focus()
    textarea.setSelectionRange(newPos, newPos)
  }, 0)
}
</script>

<style scoped>
.dm-entry {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--gray-200);
}

.dm-entry__toolbar {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
  padding: 0.3rem 0;
  border-bottom: 1px solid var(--gray-200);
}

.toolbar-btn {
  font-family: 'Roboto', sans-serif;
  min-width: 2.2rem;
}

.toolbar-btn.active {
  background: var(--halsa-green);
  color: white;
}

.toolbar-sep {
  width: 1px;
  height: 1.25rem;
  background: var(--gray-200);
  margin: 0 0.2rem;
}

.dm-entry__textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  padding: 0.75rem;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  resize: vertical;
  min-height: 5rem;
  color: var(--text-black);
  background: white;
  transition: border-color 0.15s;
}

.dm-entry__textarea:focus {
  outline: none;
  border-color: var(--halsa-green);
}

.dm-entry__preview {
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  padding: 0.75rem;
  min-height: 5rem;
  background: var(--gray-100);
}

.preview-placeholder {
  color: var(--text-grey);
  font-style: italic;
}

.dm-entry__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  gap: 0.5rem;
}

.char-count {
  font-size: 0.8rem;
  color: var(--text-grey);
}

.char-count--warn {
  color: var(--halsa-red);
}

.dm-entry__error {
  margin-top: 0.4rem;
  font-size: 0.85rem;
  color: var(--halsa-red);
}
</style>
